import { ArrowRightIcon } from "@xola/icons";
import clsx from "clsx";
import {
    DateFormat,
    formatDate,
    getGiftPurchaserAndRecipient,
    getItemLineItemsByType,
    isEquipmentItem,
    isGiftOfferProduct,
    isMembershipItem,
    isPartnerPackagePurchase,
    isScheduled,
} from "common";
import dayjs from "dayjs";
import { isEmpty, isNumber, sumBy } from "lodash";
import pluralize from "pluralize";
import React, { Children, ReactNode } from "react";
import { Except } from "type-fest";
import {
    Event,
    Experience,
    ExperiencePurchaseItem,
    GiftOffer,
    GiftPurchaseItem,
    LineItemType,
    MerchandisePurchaseItem,
    Organizer,
    Product,
    Purchase,
    PurchaseItem,
    PurchaseItemObject,
    PurchaseItemPrivacy,
    Seller,
    StoreCreditPurchaseItem,
} from "types";
import { MembershipPurchaseItem } from "types/src/types/MembershipPurchaseItem";
import { useResource } from "../../hooks/useResource";
import giftBannerBackground from "../../images/giftBannerBackground.png";
import storeCreditBannerBackground from "../../images/storeCreditBannerBackground.png";
import { isVirtualExperience } from "../../models/Experience";
import { LocalizationProps } from "../../models/Locale";
import { getDefaultPhotoUrl } from "../../models/Product";
import { getCustomerName } from "../../models/Purchase";
import { Banner } from "../Common/Banner";
import { Currency } from "../Common/Currency";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { useProduct } from "@/hooks/resources/useProduct";
import { getMerchandiseVariantName, getShortCode } from "@/models/PurchaseItem";
import { getSeller } from "@/utils/getSeller";

export interface PurchaseItemBannerProps {
    purchase: Purchase;
    item?: PurchaseItem;
    event?: Event;
    className?: string;
    style?: any;
}

export const PurchaseItemBanner = ({ purchase, item, event, ...rest }: PurchaseItemBannerProps) => {
    const currentItem = item ?? purchase.items[0];
    const isMist = !item && purchase.items.length > 1;
    const isGift = currentItem.object === PurchaseItemObject.GIFT;
    const isCredit = currentItem.object === PurchaseItemObject.STORE_CREDIT;
    const isMerchandise = currentItem.object === PurchaseItemObject.WARE;
    const isMembership = isMembershipItem(currentItem);
    const isPackage = !item && purchase.items[0].parentProduct;

    return isPackage ? (
        <ParentProductBanner purchase={purchase} item={currentItem as GiftPurchaseItem} {...rest} />
    ) : isCredit ? (
        <StoreCreditItemBanner purchase={purchase} item={currentItem} {...rest} />
    ) : isMist ? (
        <MistItemBanner purchase={purchase} {...rest} />
    ) : isGift ? (
        <GiftItemBanner purchase={purchase} item={currentItem} {...rest} />
    ) : isMembership ? (
        <MembershipItemBanner purchase={purchase} item={currentItem} {...rest} />
    ) : isMerchandise ? (
        <MerchandiseItemBanner purchase={purchase} item={currentItem} {...rest} />
    ) : (
        <SingleItemExperienceBanner
            purchase={purchase}
            item={currentItem}
            event={event}
            manual={event?.manual}
            {...rest}
        />
    );
};

interface BasicItemBannerProps extends Except<PurchaseItemBannerProps, "item"> {
    item: GiftPurchaseItem;
}

const GiftItemBanner = ({ purchase, item, ...rest }: BasicItemBannerProps) => {
    const { data: product } = useResource<Experience | GiftOffer>(["products", item.product.id]);

    const getGiftBannerBackground = () => {
        if (isGiftOfferProduct(product) && ("photo" in product || "experience" in product)) {
            return getDefaultPhotoUrl(product, "large");
        }

        return giftBannerBackground;
    };

    const discountLineItems = getItemLineItemsByType(item, LineItemType.DISCOUNT);
    const adjustedBalanceItems = getItemLineItemsByType(item, LineItemType.ADJUSTMENT).filter(
        (item) => !item.includeInAmount,
    );

    const adjustedBalanceItemsSum = item.amount ? sumBy(adjustedBalanceItems, "amount") : 0;
    const discountLineItemsSum = Math.abs(sumBy(discountLineItems, "amount"));
    const totalValue = item.amount ? item.amount + discountLineItemsSum + adjustedBalanceItemsSum : 0;

    return (
        <ItemBanner title="Gift" background={getGiftBannerBackground()} {...rest}>
            <ItemBanner.RecipientInfo {...getGiftPurchaserAndRecipient(purchase, item)} />
            <ItemBanner.Code>{item.couponCode?.code}</ItemBanner.Code>
            <ItemBanner.Balance>{item.balance}</ItemBanner.Balance>
            <ItemBanner.PurchaseAmount amount={item.amount} totalValue={totalValue} />
            <ItemBanner.PurchaseValues
                deferred={item.deferredIncome!}
                redeemed={item.redeemed!}
                recognized={item.recognizedIncome!}
            />
        </ItemBanner>
    );
};

interface StoreCreditItemBannerProps extends Except<PurchaseItemBannerProps, "item"> {
    item: StoreCreditPurchaseItem;
}

const StoreCreditItemBanner = ({ item, ...rest }: StoreCreditItemBannerProps) => {
    return (
        <ItemBanner title="Store Credit" background={storeCreditBannerBackground} {...rest}>
            <ItemBanner.Code>{item.couponCode?.code}</ItemBanner.Code>
            <ItemBanner.Balance>{item.balance}</ItemBanner.Balance>
            <ItemBanner.PurchaseValues
                deferred={item.deferredIncome!}
                redeemed={item.redeemed!}
                recognized={item.recognizedIncome!}
            />
        </ItemBanner>
    );
};

interface MembershipItemBannerProps extends Except<PurchaseItemBannerProps, "item"> {
    item: MembershipPurchaseItem;
}

const MembershipItemBanner = ({ item, ...rest }: MembershipItemBannerProps) => {
    const { data: product } = useProduct(item.product.id);
    const backgroundUrl = getDefaultPhotoUrl(product ?? item.product, "large");

    return (
        <ItemBanner title={product?.name ?? "Membership"} background={backgroundUrl} {...rest}>
            <ItemBanner.Code>
                <span className="!capitalize">
                    {item.guests.length} {pluralize("pass", item.guests.length)}
                </span>
            </ItemBanner.Code>
        </ItemBanner>
    );
};

interface MerchandiseItemBannerProps extends Except<PurchaseItemBannerProps, "item"> {
    item: MerchandisePurchaseItem;
}

const MerchandiseItemBanner = ({ purchase, item, className, style }: MerchandiseItemBannerProps) => {
    const { data: product } = useProduct(item.product.id);
    const url = getDefaultPhotoUrl(product ?? item.product, "large");
    const customerName = getCustomerName(purchase);

    const totalQuantity = sumBy(purchase.items, "quantity") || 1;
    const merchandiseVariantName = getMerchandiseVariantName(item);
    const bannerData = {
        title: product?.name ?? item.name ?? "",
        customerName: isEmpty(customerName) ? getShortCode(item)?.toUpperCase() : customerName,
        isPrivate: item.privacy === PurchaseItemPrivacy.PRIVATE,
        arrivalDate: null,
        arrivalTime: null,
        shortCode: "shortCode" in item ? item.shortCode.toUpperCase() : "Code N/A",
        variantCount: totalQuantity,
        merchandiseVariantName,
    };

    return <Banner item={item} bannerData={bannerData} url={url} className={className} style={style} />;
};

const ParentProductBanner = ({ purchase, ...rest }: PurchaseItemBannerProps) => {
    const { data: product } = useResource<Product>(["products", purchase.items[0].parentProduct!.id]);

    if (!product) {
        return null;
    }

    const url = getDefaultPhotoUrl(product, "large");

    return <ItemBanner background={url} title={product.name} {...rest} />;
};

interface ItemBannerProps extends Except<PurchaseItemBannerProps, "item" | "purchase">, LocalizationProps {
    background: string;
    title: string;
    children?: ReactNode;
}

const ItemBannerCode = ({ children }: { children: React.ReactNode }) => {
    return (
        <div
            data-testid="purchase-item-banner-gift-tag"
            className="rounded-full px-3 py-2 uppercase text-white"
            style={{ background: "rgba(34, 35, 36, 0.4)" }}
        >
            {children}
        </div>
    );
};

const ItemBannerRecipientInfo = ({ from, to }: { from?: string; to?: string }) => {
    if (!from && !to) {
        return null;
    }

    return (
        <div className="space-x-2 text-white">
            {from && (
                <span data-testid="banner-gift-from">
                    From: <NoTranslate>{from}</NoTranslate>
                </span>
            )}
            {from && to && <ArrowRightIcon />}
            {to && (
                <span data-testid="banner-gift-To">
                    To: <NoTranslate>{to}</NoTranslate>
                </span>
            )}
        </div>
    );
};

const ItemBannerBalance = ({ children }: { children: React.ReactNode }) => {
    return (
        <div className="space-x-2 text-2xl font-bold text-white" data-testid="gift-banner-balance-amount">
            <span>Balance</span>
            <Currency>{children}</Currency>
        </div>
    );
};

const ItemBannerPurchaseAmount = ({ amount, totalValue }: { amount: number; totalValue: number }) => {
    return (
        <div className="flex space-x-2 rounded-lg bg-[#000000]/40 py-2 px-3 text-sm">
            <div className="flex-col text-gray-light" data-testid="gift-banner-purchased-amount-field">
                <div>Purchase Amount</div>
                <div>Total Value</div>
            </div>
            <div className="flex-col text-white">
                <div className="font-bold" data-testid="gift-banner-purchase-amount">
                    <Currency>{amount}</Currency>
                </div>
                <div className="font-bold" data-testid="gift-Purchase-Total-value">
                    <Currency>{totalValue}</Currency>
                </div>
            </div>
        </div>
    );
};

interface ItemBannerPurchaseValuesProps {
    deferred: number;
    redeemed: number;
    recognized: number;
}

const ItemBannerPurchaseValues = ({ deferred, redeemed, recognized }: ItemBannerPurchaseValuesProps) => {
    return (
        <div className="flex space-x-2 text-sm">
            <div className="flex-col text-right text-gray-light" data-testid="gift-banner-amount-field">
                <div>Deferred</div>
                <div>Total Redeemed</div>
                <div>Recognized</div>
            </div>
            <div className="flex-col font-bold text-white">
                <div data-testid="gift-banner-deferred-amount">
                    <Currency>{deferred}</Currency>
                </div>
                <div data-testid="gift-banner-total-redeem-amount">
                    <Currency>{redeemed}</Currency>
                </div>
                <div data-testid="gift-banner-recognized-amount">
                    <Currency>{recognized}</Currency>
                </div>
            </div>
        </div>
    );
};

const ItemBanner = ({ className, background, title, children, localize, ...rest }: ItemBannerProps) => {
    const styles = {
        background: `linear-gradient(0deg, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.6)), url(${background}) 0% 20%/cover`,
    };

    const childrenArray = Children.toArray(children);

    return (
        <div
            className={clsx(
                "ui-product-banner relative flex min-h-[160px] flex-col rounded-lg bg-yellow-lightish p-8",
                className,
            )}
            style={styles}
            {...rest}
        >
            <div className="flex w-full">
                <div className="flex flex-grow space-x-4">
                    <div>
                        <div className="flex  space-x-2">
                            <div
                                className={clsx("text-2xl font-bold text-white", {
                                    "no-translate": localize === false,
                                })}
                            >
                                {title}
                            </div>
                            {childrenArray.filter((child) => (child as any)?.type === ItemBannerCode)}
                        </div>
                        {childrenArray.filter((child) => (child as any)?.type === ItemBannerRecipientInfo)}
                    </div>
                </div>
                {childrenArray.filter((child) => (child as any)?.type === ItemBannerBalance)}
            </div>
            <div className="flex w-full justify-end space-x-6">
                {childrenArray.filter((child) => (child as any)?.type === ItemBannerPurchaseAmount)}
                {childrenArray.filter((child) => (child as any)?.type === ItemBannerPurchaseValues)}
            </div>
        </div>
    );
};

ItemBanner.Code = ItemBannerCode;
ItemBanner.RecipientInfo = ItemBannerRecipientInfo;
ItemBanner.Balance = ItemBannerBalance;
ItemBanner.PurchaseAmount = ItemBannerPurchaseAmount;
ItemBanner.PurchaseValues = ItemBannerPurchaseValues;

interface SingleItemBannerProps extends Except<PurchaseItemBannerProps, "item"> {
    item: ExperiencePurchaseItem;
    manual?: boolean;
    event?: Event;
}

const SingleItemExperienceBanner = ({
    purchase,
    item,
    event,
    className,
    style,
    manual = false,
}: SingleItemBannerProps) => {
    const { data: product } = useResource<Experience>(["products", item.product.id]);
    const seller = getSeller();
    const url = getDefaultPhotoUrl(product ?? item.product, "large");
    const { arrivalDateTime, expiresAt, organizer } = item;
    const hasSchedule = product && isScheduled(product);
    const customerName = getCustomerName(purchase);
    const { data: itemSeller } = useResource<Seller>(["sellers", item.seller?.id], null, {
        enabled: item.seller?.id !== seller.id,
    });

    const demographics =
        item.lineItems?.filter(
            (lineItem) => lineItem.type === LineItemType.DEMOGRAPHIC || lineItem.type === LineItemType.EQUIPMENT,
        ) ?? [];
    const isPartnerPackageItem = item && isPartnerPackagePurchase(purchase) && item?.parentProduct;

    // use the destination seller timezone in case of partner purchases
    const itemArrivalDateTime =
        item.seller?.id !== seller.id && itemSeller
            ? dayjs(arrivalDateTime).tz(itemSeller?.timezoneName)
            : item.arrivalDateTime;

    const isVirtual = (!!event?.virtualMeeting || isVirtualExperience(product)) ?? false;

    const bannerData = {
        title: product?.name ?? "",
        customerName: isEmpty(customerName) ? getShortCode(item)?.toUpperCase() : customerName,
        isPrivate: item.privacy === PurchaseItemPrivacy.PRIVATE,
        isVirtual,
        arrivalDate: hasSchedule && arrivalDateTime ? formatDate(itemArrivalDateTime, DateFormat.DATE_LONG) : null,
        arrivalTime: isNumber(item.arrivalTime) ? formatDate(itemArrivalDateTime, DateFormat.TIME) : null,
        arrivalTimezoneName: getArrivalTimezoneName(item, isVirtual, organizer, itemSeller),
        expireDate: expiresAt ? formatDate(expiresAt, DateFormat.DATE_LONG) : null,
        isUnScheduled:
            !hasSchedule || (item?.object && item.object !== PurchaseItemObject.EXPERIENCE && !isEquipmentItem(item)),
        isManual: manual,
        shortCode: "shortCode" in item ? item.shortCode.toUpperCase() : "Code N/A",
        isPartnerExperienceBooking: !!item.partner?.id && !isPartnerPackageItem,
        isPartnerPackageBooking: !!isPartnerPackageItem,
    };

    return (
        <Banner
            item={item}
            event={event}
            experience={product}
            bannerData={bannerData}
            demographics={demographics}
            url={url}
            className={className}
            style={style}
        />
    );
};

interface BannerTileProps {
    purchase: Purchase;
    tileIndex: number;
    item: PurchaseItem;
    last: boolean;
}

const BannerTile = ({ purchase, item, tileIndex, last }: BannerTileProps) => {
    const { data: product } = useResource<Product>(["products", item.product.id]);
    const url = getDefaultPhotoUrl(product ?? item.product, "large");
    const key = `${tileIndex}-${url}`;

    return (
        <div
            key={key}
            className={clsx(
                "h-full",
                tileIndex === 0 && "rounded-tl-lg rounded-bl-lg",
                last && "rounded-tr-lg rounded-br-lg",
            )}
            style={{
                background: `linear-gradient(0deg, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.6)), url(${url}) 0% 20%/cover`,
                width: `calc(100% / ${purchase.items.length})`,
            }}
        />
    );
};

const MistItemBanner = ({ purchase, className, ...rest }: Except<PurchaseItemBannerProps, "item">) => {
    return (
        <div
            className={clsx("ui-product-banner relative flex h-40 min-h-[160px] rounded-lg text-white", className)}
            {...rest}
        >
            {purchase.items.map((purchaseItem, index) => (
                <BannerTile
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    purchase={purchase}
                    tileIndex={index}
                    item={purchaseItem}
                    last={index === purchase.items.length - 1}
                />
            ))}

            <div
                className="absolute left-7 bottom-7 text-2xl font-bold"
                data-testid="purchase-item-banner-reservation-items-length"
            >
                <NoTranslate>{purchase.items.length}</NoTranslate> Reservation Purchase
            </div>
        </div>
    );
};

const getArrivalTimezoneName = (item: PurchaseItem, isVirtual: boolean, organizer: Organizer, itemSeller?: Seller) => {
    const sellerId = getSeller().id;
    if (item.seller?.id !== sellerId && itemSeller) {
        return itemSeller?.timezoneName;
    }

    if (isVirtual) {
        return organizer.timezoneName;
    }

    return undefined;
};
